import { Fieldset } from '@components/Fields/Fieldset';
import { Box, Grid, makeStyles, MenuItem, Typography } from '@material-ui/core';
import React from 'react';
import SearchIcon from '@material-ui/icons/Search';
import { FloatField } from '@components/Fields/FloatField';
import { IForm } from '@hooks/useForm';
import { EnumModoFechamentoNegativo, IRegistrarPagamento } from '@services/FaturaService';
import { EnumFormaPagamento, IComboFormaPagamento } from '@model/FormaPagamento';
import { ComboField } from '@components/Fields/ComboField';
import { DateField } from '@components/Fields/DateField';
import useFormatter from '@hooks/useFormatter';
import { CheckboxField } from '@components/Fields/CheckboxField';
import { VerticalGrid } from '@components/VerticalGrid';
import { HorizontalGrid } from '@components/HorizontalGrid';
import { GridItem } from '@components/GridItem';
import PaymentIcon from '@material-ui/icons/Payment';
import ReceiptIcon from '@material-ui/icons/Receipt';
import { StringField } from '@components/Fields/StringField';
import NoteIcon from '@material-ui/icons/Note';
import { IField } from '@hooks/useField';

export type RegistrarPagamentoFormProps = {
    form: IForm<IRegistrarPagamento>
    formaPagamentoCombo: IComboFormaPagamento
    confirmSectionVisible: boolean
    valorAtualField: IField
    saldoAtualField: IField
}

export function RegistrarPagamentoForm ({ form, formaPagamentoCombo, confirmSectionVisible, valorAtualField, saldoAtualField }: RegistrarPagamentoFormProps) {
    const cls = useStyles();
    const formatter = useFormatter();
    const  { fields } = form;

    React.useEffect(() => {
        const hasDateField = fields.formaPagamento.value !== EnumFormaPagamento.None && fields.formaPagamento.value !== EnumFormaPagamento.Caixa;

        if (!hasDateField) {
            fields.dataPagamento.setValue(null);
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields.formaPagamento.value]);
  
    return (
        <VerticalGrid>
            <Fieldset
                icon={<ReceiptIcon />}
                title="Situação Atual"
                hasMarginTop={false}
            >
                <HorizontalGrid spacing={1}>
                    <GridItem xs={4}>
                        <FloatField 
                            field={fields.valorOriginal}
                            label="Valor Original"
                            disabled
                        />
                    </GridItem>

                    <GridItem xs={4}>
                        <FloatField 
                            field={fields.valorMulta}
                            label="Multa"
                            disabled
                        />
                    </GridItem>

                    <GridItem xs={4}>
                        <FloatField 
                            field={fields.valorJuros}
                            label="Juros Acumulados"
                            disabled
                        />
                    </GridItem>

                    <GridItem xs={12}>
                        <Box sx={{textAlign: 'right'}}>
                            <CheckboxField
                                field={fields.abonarJurosMulta}
                                label="Abonar juros e multa dessa fatura"
                                disabled={fields.abonarJurosMultaDesabilitado.value}
                                updateFieldStateOnChange
                            />
                        </Box>
                    </GridItem>

                    <GridItem xs={4}>
                        <FloatField 
                            field={valorAtualField}
                            label="Valor Atual"
                            disabled
                        />
                    </GridItem>

                    <GridItem xs={4}>
                        <FloatField 
                            field={fields.valorPago}
                            label="Valor Já Pago"
                            disabled
                        />
                    </GridItem>

                    <GridItem xs={4}>
                        <FloatField 
                            field={saldoAtualField}
                            label="Saldo Restante"
                            disabled
                        />
                    </GridItem>
                </HorizontalGrid>
            </Fieldset>

            <Fieldset
                icon={<PaymentIcon />}
                title="Pagamento"
            >
                <HorizontalGrid spacing={1}>
                    <GridItem xs={4}>
                        <FloatField 
                            field={fields.valorRecebido}
                            label="Valor Recebido"
                            updateFieldStateOnChange
                        />
                    </GridItem>

                    <GridItem xs={4}>
                        <FloatField 
                            field={fields.novoSaldo}
                            label="Novo Saldo"
                            disabled
                        />
                    </GridItem>

                    <GridItem xs={4}>
                        <ComboField 
                            field={fields.formaPagamento}
                            label="FormaPagamento"
                            updateFieldStateOnChange
                        >
                        {
                            formaPagamentoCombo.createOptions()
                        }
                        </ComboField>
                    </GridItem>

                    {
                        fields.formaPagamento.value !== EnumFormaPagamento.None && fields.formaPagamento.value !== EnumFormaPagamento.Caixa && (
                            <GridItem xs={12}>
                                <HorizontalGrid spacing={1}>
                                    <GridItem xs={4} floatRight>
                                        <DateField 
                                            field={fields.dataPagamento}
                                            label="Data Pagamento"
                                            disableFuture
                                            updateFieldStateOnChange
                                        />
                                    </GridItem>
                                </HorizontalGrid>
                            </GridItem>
                        )
                    }
                </HorizontalGrid>
            </Fieldset>

            <Fieldset
                icon={<NoteIcon />}
                title="Notas"
            >
                <HorizontalGrid spacing={1}>
                    <GridItem xs={12}>
                        <StringField 
                            field={fields.notas}
                            multiline
                        />
                    </GridItem>
                </HorizontalGrid>
            </Fieldset>

            {
                confirmSectionVisible && (
                    <Fieldset
                        icon={<SearchIcon />}
                        title="Confirmação"
                    >
                        <Grid container>
                            <Grid item xs={12}>
                            {
                                fields.novoSaldo.value < 0 && ( //tem saldo negativo então ele pagou a mais
                                    <CheckboxField 
                                        field={form.fields.cienteCreditoCliente}
                                        updateFieldStateOnChange
                                        label={(
                                            <HorizontalGrid>
                                                <Typography>Será</Typography> 
                                                <Typography className={cls.somado}> somado </Typography>
                                                <Typography>{` ${formatter.toFloat(-(fields.novoSaldo.value || 0))} ao crédito do cliente`}</Typography>
                                            </HorizontalGrid>
                                        )}
                                    />
                                )
                            }

                            {
                                fields.novoSaldo.value > 0.001 && ( //ainda tem saldo então ele tá devendo
                                    <ComboField
                                        field={form.fields.modoFechamentoNegativo}
                                        updateFieldStateOnChange
                                        placeholder="A fatura ainda possui saldo. Por favor, escolha uma ação"
                                    >
                                        <MenuItem key={1} value={EnumModoFechamentoNegativo.DeixarFaturaAberta}>Deixar a fatura aberta e fechá-la depois</MenuItem>
                                        <MenuItem key={2} value={EnumModoFechamentoNegativo.FecharFaturaSubtrairSaldo}>
                                            <HorizontalGrid>
                                                <Typography>Fechar a fatura e</Typography>
                                                <Typography className={cls.subtraido}>subtrair</Typography>
                                                <Typography>{`${formatter.toFloat((fields.novoSaldo.value || 0))}`} do crédito do cliente</Typography>
                                            </HorizontalGrid>
                                        </MenuItem>
                                    </ComboField>
                                )
                            }
                            </Grid>
                        </Grid>
                    </Fieldset>
                )
            }
        </VerticalGrid>
    )
}

const useStyles = makeStyles(theme => ({
    subtraido: {
        color: 'red',
        fontWeight: 'bold',
        marginLeft: 5,
        marginRight: 5
    },

    somado: {
        color: 'darkgreen',
        fontWeight: 'bold',
        marginLeft: 5,
        marginRight: 5
    }
}));